import type {
  ScheduledEventStatus,
  TrackingPageScheduledEvent,
} from "@redotech/redo-flows-common/tracking-page-scheduled-event";
import {
  getScheduledEventStatus,
  scheduledEventsOverlap,
} from "@redotech/redo-flows-common/tracking-page-scheduled-event";
import { t, useTranslation } from "@redotech/redo-i18n";
import CalendarDateIcon from "@redotech/redo-icons/calendar-date.svg";
import DotsHorizontalIcon from "@redotech/redo-icons/dots-horizontal.svg";
import EditPencilIcon from "@redotech/redo-icons/edit-05.svg";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import TrashIcon from "@redotech/redo-icons/trash-03.svg";
import type { RedoBadgeColor } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCommandMenu } from "@redotech/redo-web/arbiter-components/command-menu/redo-command-menu";
import { RedoEmptyState } from "@redotech/redo-web/arbiter-components/empty-state/redo-empty-state";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { useState } from "react";
import type { DeepPartial } from "..";
import type { TrackingPageOption } from "./tracking-page-picker";

export type PartialScheduledEvent = DeepPartial<TrackingPageScheduledEvent>;

export function isCompleteScheduledEvent(
  event: PartialScheduledEvent,
): event is TrackingPageScheduledEvent {
  return (
    !!event.id &&
    !!event.name &&
    !!event.trackingPageId &&
    !!event.startAt &&
    !!event.endAt &&
    !!event.timeZone
  );
}

function statusBadge(status: ScheduledEventStatus): {
  color: RedoBadgeColor;
  text: string;
} {
  switch (status) {
    case "live":
      return { color: "success", text: t("Live now") };
    case "scheduled":
      return { color: "blue", text: t("Scheduled") };
    case "finished":
      return { color: "gray", text: t("Finished") };
  }
}

function statusCaption(status: ScheduledEventStatus): string {
  switch (status) {
    case "scheduled":
      return t("Starts automatically");
    // An upstream branch decides what share of traffic reaches this step.
    case "live":
      return t("Replaces this step's page for all traffic while active");
    case "finished":
      return t("Ended automatically");
  }
}

function formatEventInstant(value: Date): string {
  const date = new Date(value);
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    ...(date.getFullYear() !== new Date().getFullYear() && { year: "numeric" }),
    hour: "numeric",
    minute: "numeric",
  }).format(date);
}

/** Scheduled-event rows: window, status, and edit or remove. */
export function ScheduledEventsSection({
  events,
  trackingPages,
  onCreateEvent,
  onEditEvent,
  onRemoveEvent,
  disabled,
  showValidationErrors,
}: {
  events: PartialScheduledEvent[];
  /** For resolving each event's page name; undefined while loading. */
  trackingPages: TrackingPageOption[] | undefined;
  onCreateEvent: () => void;
  onEditEvent: (eventId: string) => void;
  onRemoveEvent: (eventId: string) => void;
  disabled?: boolean;
  showValidationErrors?: boolean;
}) {
  const { t } = useTranslation();
  const completeEvents = events.filter(isCompleteScheduledEvent);
  const hasIncompleteEvent = completeEvents.length < events.length;
  const overlapping = scheduledEventsOverlap(completeEvents);
  const now = new Date();
  const rank = (event: PartialScheduledEvent) => {
    if (!isCompleteScheduledEvent(event)) {
      return 1;
    }
    const status = getScheduledEventStatus(event, now);
    return status === "live" ? 0 : status === "scheduled" ? 1 : 2;
  };
  const startMs = (event: PartialScheduledEvent) =>
    event.startAt ? new Date(event.startAt).getTime() : Number.MAX_SAFE_INTEGER;
  const sortedEvents = [...events].sort((a, b) => {
    const rankA = rank(a);
    const rankDiff = rankA - rank(b);
    if (rankDiff !== 0) {
      return rankDiff;
    }
    return rankA === 2 ? startMs(b) - startMs(a) : startMs(a) - startMs(b);
  });

  return (
    <Flex dir="column" gap="xxs">
      <Flex align="center" dir="row" justify="space-between" mb="md">
        <Text fontSize="xs" textColor="secondary">
          {t("Scheduled")}
        </Text>
        <RedoButton
          disabled={disabled}
          hierarchy="secondary"
          IconLeading={PlusIcon}
          onClick={onCreateEvent}
          size="xs"
          text={t("Event")}
        />
      </Flex>

      {events.length === 0 ? (
        <RedoEmptyState
          Icon={CalendarDateIcon}
          primaryButtonProps={
            disabled
              ? undefined
              : { onClick: onCreateEvent, text: t("Schedule event") }
          }
          subtext={t(
            "Show a different tracking page during a promotion window.",
          )}
          text={t("No scheduled events")}
          useNonArbiterPadding
        />
      ) : (
        <Flex dir="column" gap="sm" maxHeight="quarter-screen" overflowY="auto">
          {sortedEvents.map((event, index) => (
            <ScheduledEventRow
              disabled={disabled}
              event={event}
              key={event.id ?? index}
              onEdit={event.id ? () => onEditEvent(event.id!) : undefined} // eslint-disable-line @typescript-eslint/no-non-null-assertion
              onRemove={event.id ? () => onRemoveEvent(event.id!) : undefined} // eslint-disable-line @typescript-eslint/no-non-null-assertion
              trackingPages={trackingPages}
            />
          ))}
        </Flex>
      )}

      {showValidationErrors && overlapping && (
        <Text fontSize="xs" textColor="error">
          {t(
            "Two scheduled events overlap. Adjust their dates so only one is live at a time.",
          )}
        </Text>
      )}
      {showValidationErrors && hasIncompleteEvent && (
        <Text fontSize="xs" textColor="error">
          {t(
            "A scheduled event is missing a name, date range, or tracking page.",
          )}
        </Text>
      )}
    </Flex>
  );
}

function ScheduledEventRow({
  event,
  trackingPages,
  onEdit,
  onRemove,
  disabled,
}: {
  event: PartialScheduledEvent;
  trackingPages: TrackingPageOption[] | undefined;
  onEdit?: () => void;
  onRemove?: () => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLButtonElement | null>(null);

  const complete = isCompleteScheduledEvent(event);
  const status = complete
    ? getScheduledEventStatus(event, new Date())
    : undefined;
  const badge = status ? statusBadge(status) : undefined;
  const caption = status ? statusCaption(status) : undefined;
  const eventPage =
    event.trackingPageId && trackingPages
      ? trackingPages.find((p) => p._id === event.trackingPageId)
      : undefined;
  const pageDeleted = !!event.trackingPageId && !!trackingPages && !eventPage;

  return (
    <Flex bgColor="secondary" borderRadius="md" dir="column" gap="xs" p="md">
      <Flex align="flex-start" dir="row" gap="md" justify="space-between">
        <Flex dir="column" gap="xxs" grow={1} overflow="hidden">
          <Text
            fontSize="sm"
            fontWeight="medium"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {event.name || t("Untitled event")}
          </Text>
          {event.startAt && event.endAt && (
            <Text fontSize="xs" textColor="secondary">
              {formatEventInstant(event.startAt)} to{" "}
              {formatEventInstant(event.endAt)}
            </Text>
          )}
          {pageDeleted && (
            <Text fontSize="xs" textColor="error">
              {t(
                "Page no longer exists — the team's default page will show instead.",
              )}
            </Text>
          )}
          {caption && (
            <Text fontSize="xs" textColor="secondary">
              {caption}
            </Text>
          )}
        </Flex>
        <Flex align="center" dir="row" gap="xs">
          {badge && (
            <RedoBadge color={badge.color} size="xs" text={badge.text} />
          )}
          {onEdit && onRemove && (
            <>
              <RedoButton
                aria-label={t("More options")}
                disabled={disabled}
                hierarchy="tertiary"
                IconLeading={DotsHorizontalIcon}
                onClick={() => setMenuOpen(true)}
                ref={setMenuAnchor}
                size="xs"
              />
              <RedoCommandMenu
                anchor={menuAnchor}
                items={[
                  ...(status !== "finished"
                    ? [
                        {
                          Icon: EditPencilIcon,
                          onClick: onEdit,
                          text: t("Edit"),
                        },
                      ]
                    : []),
                  {
                    Icon: TrashIcon,
                    onClick: onRemove,
                    text: t("Remove"),
                    theme: "destructive" as const,
                  },
                ]}
                open={menuOpen}
                setOpen={setMenuOpen}
              />
            </>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
}
